import Konva from "konva";
import { Vector2d } from "konva/lib/types";
import { useRef, useState } from "react";
import { Group, Label, Rect, Tag, Text } from "react-konva";
import { Portal } from "react-konva-utils";
import {
  endDragRectangle,
  openRectContextMenu,
  startDragRectangle,
  toggleRectInSelection,
} from "@features/annogram/canvas/state/canvasAuxSlice";
import {
  addArrow,
  moveRectangle,
  toggleRectangleExpansion,
} from "@store/slices/workspaceSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectArgumentEvaluation,
  selectViewedRectangles,
  selectCategories,
  selectPageByAnnotationId,
} from "@store/selectors";
import type { NodeDiagnostic } from "@features/annogram/canvas/utils/argumentChecks";
import { ICanvasArrow, Rectangle } from "@/types/canvasTypes";
import { Category } from "@/types/models";
import {
  canvasDarkText,
  canvasTextFontFamily,
  canvasTextFontSize,
  labelRectCornerRadius,
  labelRectPadding,
  pageBadgeHeadroom,
  textRectPadding,
} from "@shared/utils/consts";
import {
  calculateExpandedTextSize,
  collapsedDisplayBody,
  collapsedRectSize,
  dashStateForCredence,
  rectsHaveIntersection,
} from "@shared/utils/utils";
import { useCanvasFontReady } from "@shared/hooks/useCanvasFontReady";
import CanvasArrow from "./CanvasArrow";
import CanvasImage from "./CanvasImage";

const findCategoryColor = (categories: Category[], rectangle: Rectangle) => {
  return categories.find((category) => category.label === rectangle.category)
    ?.background;
};

/**
 * Map a rect's worst diagnostic to a glow color.
 *
 * Priority: cycle-family (red) > other [3] (orange) > [1] (blue).
 * Petitio principii is structurally a cycle involving the target, so it
 * groups with [4] under red rather than the generic [3] orange.
 *
 * - red    (#d32f2f) — circular reasoning, petitio principii
 * - orange (#ed6c02) — ignoratio elenchi, ambiguous conclusion
 * - blue   (#1976d2) — begging the question (setting-completeness)
 */
const diagnosticShadowColor = (
  diagnostics: NodeDiagnostic[] | undefined
): string | null => {
  if (!diagnostics || diagnostics.length === 0) return null;
  if (
    diagnostics.some(
      (d) => d.condition === "4" || d.fallacy === "petitio_principii"
    )
  ) {
    return "#d32f2f";
  }
  if (diagnostics.some((d) => d.condition === "3")) return "#ed6c02";
  if (diagnostics.some((d) => d.condition === "1")) return "#1976d2";
  return null;
};

const formatTooltip = (diagnostics: NodeDiagnostic[]): string =>
  diagnostics.map((d) => d.message).join("\n\n");

const TOOLTIP_WIDTH = 280;

// Shared measurer for the provenance page badge — Konva has no
// intrinsic right-align-in-parent, so we size the badge to its text.
const BADGE_FONT_SIZE = 11.5;
const badgeMeasurer = new Konva.Text({
  fontSize: BADGE_FONT_SIZE,
  fontFamily: canvasTextFontFamily,
});
const measureBadgeWidth = (t: string) => {
  badgeMeasurer.text(t);
  return badgeMeasurer.width();
};

interface CanvasRectangleProps {
  idx: number;
}
const CanvasRectangle = ({ idx }: CanvasRectangleProps) => {
  const [originCoords, setOriginCoords] = useState<Vector2d>({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const rectangles = useAppSelector(selectViewedRectangles);
  const categories = useAppSelector(selectCategories);
  const themeState = useAppSelector((state) => state.theme.mode);
  const { draggedRectIdx, selectedRectIndices } = useAppSelector(
    (state) => state.canvasAux
  );
  const evaluation = useAppSelector(selectArgumentEvaluation);
  const pageByAnnotationId = useAppSelector(selectPageByAnnotationId);
  const fontReady = useCanvasFontReady();
  const dispatch = useAppDispatch();

  const rectangle = rectangles[idx];
  // Tracks "is this rect selected" so we can render a selection halo
  // and decide whether dragging this rect should pull the rest of the
  // selection along.
  const isSelected = selectedRectIndices.includes(idx);
  // On a multi-rect drag start we snapshot every selected rect's
  // (x, y). Each move event then translates the OTHER selected rects
  // by the delta between the dragged rect's current and starting
  // position. Without snapshots the deltas would accumulate.
  const selectionStartRef = useRef<Map<number, { x: number; y: number }> | null>(
    null
  );

  if (!rectangle) return null;

  const { text, expanded, image, x, y, width, height, tag, arrows } = rectangle;

  // Provenance page badge. The value stored at drop time wins; rects
  // predating source_page_number fall back to the page of their source
  // highlight while that highlight still exists. Direct-created rects
  // (no provenance) show no badge.
  const sourcePage =
    rectangle.source_page_number ??
    (rectangle.source_annotation_client_id
      ? pageByAnnotationId[rectangle.source_annotation_client_id]
      : undefined);

  const isDragged = draggedRectIdx === idx;

  if (!image && !text) return null;

  const handleContextMenu = (event: Konva.KonvaEventObject<MouseEvent>) => {
    event.evt.preventDefault();
    dispatch(openRectContextMenu({ idx }));
  };

  const handleClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    // Cmd/Ctrl-click toggles this rect's selection without disturbing
    // the rest. A plain click does nothing here — the existing single-
    // select behavior (open the rect on click, etc.) lives elsewhere.
    if (event.evt.metaKey || event.evt.ctrlKey) {
      event.cancelBubble = true;
      dispatch(toggleRectInSelection(idx));
    }
  };

  // True iff this drag should pull other selected rects along. We use
  // it both to set up the snapshot AND to suppress the overlap-creates-
  // arrows logic on dragEnd — turning a multi-drag into a flurry of
  // accidental arrows would be a terrible UX.
  const isMultiDrag = () => isSelected && selectedRectIndices.length > 1;

  const handleRectangleGroupDragStart = (
    event: Konva.KonvaEventObject<DragEvent>
  ) => {
    const x = event.target.x();
    const y = event.target.y();
    dispatch(startDragRectangle({ idx }));
    dispatch(moveRectangle({ x, y, idx }));
    setOriginCoords({ x, y });

    if (isMultiDrag()) {
      const starts = new Map<number, { x: number; y: number }>();
      for (const sIdx of selectedRectIndices) {
        const r = rectangles[sIdx];
        if (r) starts.set(sIdx, { x: r.x, y: r.y });
      }
      selectionStartRef.current = starts;
    } else {
      selectionStartRef.current = null;
    }
  };

  const handleRectangleGroupDragMove = (
    event: Konva.KonvaEventObject<DragEvent>
  ) => {
    const newX = event.target.x();
    const newY = event.target.y();
    dispatch(moveRectangle({ x: newX, y: newY, idx }));

    const starts = selectionStartRef.current;
    if (!starts) return;
    const myStart = starts.get(idx);
    if (!myStart) return;
    const dx = newX - myStart.x;
    const dy = newY - myStart.y;
    starts.forEach((s, otherIdx) => {
      if (otherIdx === idx) return;
      dispatch(
        moveRectangle({ idx: otherIdx, x: s.x + dx, y: s.y + dy })
      );
    });
  };

  function addArrows(idx: number, targetIds: number[]) {
    for (let i = 0; i < targetIds.length; i++) {
      const targetId = targetIds[i];
      if (targetId === undefined) continue;

      // Skip if there's already an edge in this direction. This naturally
      // enforces the 2-edges-per-pair limit: at most one edge idx→targetId
      // and one edge targetId→idx, drawn as parallel offset lines.
      const hasArrow = rectangles[idx]?.arrows.find(
        ({ endRectIdx: end }) => end === targetId
      );
      if (hasArrow) continue;

      dispatch(
        addArrow({
          startRectIdx: idx,
          endRectIdx: targetId,
          credence: 100,
          kind: "undirected",
          tag: "",
        })
      );
    }
  }

  const getOverlappedObjectIdx = (startRectIdx: number) => {
    const startRect = rectangles[startRectIdx];
    if (!startRect) return [];
    return rectangles.reduce((acc: number[], endRect, endRectIdx): number[] => {
      if (!endRect || endRectIdx === startRectIdx) return acc;
      if (rectsHaveIntersection(startRect, endRect)) {
        acc.push(endRectIdx);
      }

      return acc;
    }, []);
  };

  const handleRectangleGroupDragEnd = (
    event: Konva.KonvaEventObject<DragEvent>
  ) => {
    // Multi-drag: skip the overlap-as-arrow-creation path entirely.
    // Translating a selection of rects past other rects shouldn't
    // produce an explosion of edges.
    if (selectionStartRef.current) {
      selectionStartRef.current = null;
      dispatch(endDragRectangle());
      dispatch(
        moveRectangle({
          x: event.target.x(),
          y: event.target.y(),
          idx,
        })
      );
      return;
    }

    const overlappedIndices = getOverlappedObjectIdx(idx);
    // we need to complete the move if there are no overlaps
    if (!overlappedIndices.length) {
      dispatch(endDragRectangle());
      dispatch(
        moveRectangle({
          x: event.target.x(),
          y: event.target.y(),
          idx,
        })
      );
      return;
    }

    const { x, y } = originCoords;

    // animation to original position is needed for state updating reasons
    event.target.to({
      x,
      y,
      duration: 0,
    });

    addArrows(idx, overlappedIndices);
    dispatch(endDragRectangle());
    dispatch(
      moveRectangle({
        x,
        y,
        idx,
      })
    );
  };

  // Collapsed body is the label; expanded body is the full annotation
  // text. Legacy rects without an explicit tag fall back to an
  // auto-derived label so the visual stays compact instead of dumping
  // the entire paragraph into the node.
  const collapsedBody = collapsedDisplayBody(tag, text);

  const handleRectangleGroupDoubleClick = () => {
    if (!text) return;

    const size = expanded
      ? collapsedRectSize(collapsedBody || text, sourcePage !== undefined)
      : calculateExpandedTextSize(text);
    dispatch(toggleRectangleExpansion({ idx, ...size }));
  };

  const { dragged, immobile } = sortArrowsIntoBuckets(
    arrows,
    isDragged,
    draggedRectIdx
  );

  // Statement credence renders as a dashed border, same visual language
  // as arrow (inference) credence but a fully independent value.
  const credenceDash = dashStateForCredence(rectangle.credence);

  const myDiagnostics = evaluation?.byRect.get(idx);
  const diagShadowColor = diagnosticShadowColor(myDiagnostics);
  // Konva's shadowBlur is a Gaussian blur and gets repainted every drag
  // frame, which causes visible cursor lag. Suppress the diagnostic glow
  // on the rect actually being dragged; restore it on dragEnd. Other rects
  // keep their glow.
  const showDiagShadow = diagShadowColor !== null && !isDragged;
  const showTooltip =
    isHovered &&
    !isDragged &&
    myDiagnostics !== undefined &&
    myDiagnostics.length > 0;

  return (
    <>
      <Portal selector=".dragging-layer" enabled={isDragged}>
        <Group
          draggable
          x={x}
          y={y}
          key={idx}
          onClick={handleClick}
          onDragStart={(e) => {
            setIsHovered(false);
            handleRectangleGroupDragStart(e);
          }}
          onDragMove={handleRectangleGroupDragMove}
          onDragEnd={handleRectangleGroupDragEnd}
          onContextMenu={handleContextMenu}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Rect
            fill={
              themeState === "dark" ? "rgb(40, 40, 40)" : "rgb(249, 248, 246)"
            }
            stroke={findCategoryColor(categories, rectangle) || "#ddcc77"}
            strokeWidth={3.5}
            dashEnabled={credenceDash.enabled}
            dash={credenceDash.pattern}
            shadowEnabled={showDiagShadow || isDragged}
            shadowColor={showDiagShadow ? diagShadowColor! : "black"}
            shadowBlur={showDiagShadow ? 16 : 0}
            shadowOffset={
              showDiagShadow ? { x: 0, y: 0 } : { x: 3, y: 3 }
            }
            shadowOpacity={showDiagShadow ? 0.85 : 0.25}
            width={width}
            height={height}
            cornerRadius={expanded ? 5 : labelRectCornerRadius}
          />
          {isSelected && (
            <Rect
              x={-3}
              y={-3}
              width={width + 6}
              height={height + 6}
              cornerRadius={7}
              stroke="#1976d2"
              strokeWidth={2}
              dash={[6, 4]}
              listening={false}
            />
          )}
          {text ? (
            <Text
              // Remount once when the webfont lands so Konva re-measures
              // line wrapping with real metrics (see useCanvasFontReady).
              key={fontReady ? "font-ready" : "font-loading"}
              text={expanded ? text : collapsedBody}
              align={expanded ? "left" : "center"}
              wrap="word"
              fontFamily={canvasTextFontFamily}
              fontSize={canvasTextFontSize}
              fill={themeState === "dark" ? "white" : canvasDarkText}
              verticalAlign="middle"
              // Collapsed rects with a page badge draw the label in the
              // area above the bottom badge band, so the two can never
              // overlap regardless of how tight the stored size is.
              width={width}
              height={
                !expanded && sourcePage !== undefined
                  ? Math.max(0, height - pageBadgeHeadroom)
                  : height
              }
              padding={expanded ? textRectPadding : labelRectPadding}
              onDblClick={handleRectangleGroupDoubleClick}
            />
          ) : image ? (
            <CanvasImage dataurl={image} />
          ) : null}
          {sourcePage !== undefined &&
            !expanded &&
            (() => {
              const badgeText = `p. ${sourcePage}`;
              const bw = measureBadgeWidth(badgeText) + 12;
              const bh = 19;
              // Inset in the bottom-right corner, inside the headroom
              // band that collapse/creation sizing reserves for it.
              return (
                <Group x={width - bw - 4} y={height - bh - 4} listening={false}>
                  {/* Backing in the rect's own fill: invisible on text
                      rects, a matching notch over images. */}
                  <Rect
                    width={bw}
                    height={bh}
                    cornerRadius={4}
                    fill={
                      themeState === "dark"
                        ? "rgb(40, 40, 40)"
                        : "rgb(249, 248, 246)"
                    }
                  />
                  <Text
                    text={badgeText}
                    fontFamily={canvasTextFontFamily}
                    fontSize={BADGE_FONT_SIZE}
                    fill={themeState === "dark" ? "white" : "black"}
                    width={bw}
                    height={bh}
                    align="center"
                    verticalAlign="middle"
                  />
                </Group>
              );
            })()}
        </Group>
      </Portal>
      <Portal selector=".dragging-layer" enabled={dragged.length > 0}>
        {dragged.map(({ endRectIdx, ...rest }) => (
          <CanvasArrow
            startRectIdx={idx}
            endRectIdx={endRectIdx}
            key={idx + "." + endRectIdx}
            {...rest}
          />
        ))}
      </Portal>
      <Portal selector=".arrow-layer" enabled>
        {immobile.map(({ endRectIdx, ...rest }) => (
          <CanvasArrow
            startRectIdx={idx}
            endRectIdx={endRectIdx}
            key={idx + "." + endRectIdx}
            {...rest}
          />
        ))}
      </Portal>
      {/*
        Tooltip lives on the topmost dragging-layer so it always renders above
        every rectangle, regardless of canvas iteration order. Positioned just
        below the rect to avoid needing to measure its rendered height.
      */}
      {showTooltip && myDiagnostics && (
        <Portal selector=".dragging-layer" enabled>
          <Label x={x} y={y + height + 6} listening={false}>
            <Tag
              fill={"rgb(40,40,40)"}
              cornerRadius={4}
              opacity={0.95}
            />
            <Text
              text={formatTooltip(myDiagnostics)}
              fontFamily={canvasTextFontFamily}
              fontSize={12}
              fill={"#fff"}
              padding={8}
              width={TOOLTIP_WIDTH}
              wrap="word"
              lineHeight={1.3}
            />
          </Label>
        </Portal>
      )}
    </>
  );
};

interface ArrowRenderBuckets {
  dragged: ICanvasArrow[];
  immobile: ICanvasArrow[];
}
/**
 * Sorting arrows into buckets enables the dragged arrows to be rendered
 * on top of immobile rectangles. It should(?) also bring some performance benefits.
 * @param arrows neighbors of the rectangle
 * @param isDragged whether the rectangle is being dragged
 * @param draggedRectIdx which rectangle is being dragged
 * @returns two buckets of arrows
 */
const sortArrowsIntoBuckets = (
  arrows: ICanvasArrow[],
  isDragged: boolean,
  draggedRectIdx: number | null
): ArrowRenderBuckets => {
  if (draggedRectIdx === null)
    return {
      dragged: [],
      immobile: arrows.filter((arrow) => !arrow.doNotRender),
    };

  if (isDragged)
    return {
      dragged: arrows.filter((arrow) => !arrow.doNotRender),
      immobile: [],
    };

  return arrows.reduce<ArrowRenderBuckets>(
    (acc, arrow) => {
      const { endRectIdx, doNotRender } = arrow;
      if (doNotRender) return acc;
      if (draggedRectIdx === endRectIdx) {
        acc.dragged.push(arrow);
      } else {
        acc.immobile.push(arrow);
      }
      return acc;
    },
    { dragged: [], immobile: [] }
  );
};

export default CanvasRectangle;
